import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { Avatar, Button, Dropdown, Flex, MenuProps, Space, Tooltip, Typography, theme } from "antd";
import {
    UserOutlined,
    DownloadOutlined,
    MedicineBoxOutlined,
    CloseCircleOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    EllipsisOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { BackButton } from "../../../../components/backButton";
import { StatusLabels } from "../statusLabels";
import { useAvatarPalette } from "../../../../shared/styles";
import { getAvatarColor, getInitials, getInitialsFromName } from "../../../../shared/utils";
import { AdminRole, TunerRole } from "../../../../models/user";

export interface OrderShowBarProps {
    record: any;
    orderStatus: string;
    canShowAssignToMe: boolean;
    isAssigningToMe: boolean;
    onAssignToMe: () => void;
    onOpenSettingsModal: () => void;
    isAdmin: boolean;
    isTuner: boolean;
    assignedUserId?: number;
    assignedServiceProviderId?: number;
    assignedGroupId?: number;
    assignmentStatus?: string;
    usersData?: any;
    serviceProvidersData?: any;
    groupsData?: any;
    onOpenAssignmentModal?: () => void;
    user: any;
    onDownloadIni: () => void;
    onDownloadIniAndClientFiles: () => void;
    onOpenSAV: () => void;
    onCloseSAV: () => void;
    hasAvailableAutomation: boolean;
    onOpenAutomationModal: () => void;
    isMobile?: boolean;
}

export const OrderShowBar: React.FC<OrderShowBarProps> = ({
    record,
    orderStatus,
    canShowAssignToMe,
    isAssigningToMe,
    onAssignToMe,
    onOpenSettingsModal,
    isAdmin,
    isTuner,
    assignedUserId,
    assignedServiceProviderId,
    assignedGroupId,
    assignmentStatus,
    usersData,
    serviceProvidersData,
    groupsData,
    onOpenAssignmentModal,
    user,
    onDownloadIni,
    onDownloadIniAndClientFiles,
    onOpenSAV,
    onCloseSAV,
    hasAvailableAutomation,
    onOpenAutomationModal,
    isMobile = false,
}) => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const avatarPalette = useAvatarPalette();

    const canDownload = isAdmin || (isTuner && assignedUserId && assignedUserId !== (user as { id?: number })?.id);

    const tuners = useMemo(() => {
        return usersData?.data?.filter(
            (u: any) => u.role?.id === TunerRole.id || u.role?.id === AdminRole.id
        ) || [];
    }, [usersData]);

    const assignedEntity = useMemo(() => {
        if (assignedUserId) {
            const u = tuners.find((t: any) => t.id === assignedUserId);
            if (u) {
                const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
                return {
                    name: name || u.email || `User ${u.id}`,
                    initials: getInitials(u.firstName, u.lastName),
                    color: getAvatarColor(u.id, avatarPalette),
                    subtitle: u.role?.name,
                };
            }
        }
        if (assignedGroupId) {
            const g = groupsData?.data?.find((g: any) => g.id === assignedGroupId);
            if (g) return {
                name: g.name,
                initials: getInitialsFromName(g.name),
                color: getAvatarColor(g.id, avatarPalette),
                subtitle: `${g.users?.length || 0} membres`,
            };
        }
        if (assignedServiceProviderId) {
            const p = serviceProvidersData?.data?.find((p: any) => p.id === assignedServiceProviderId);
            if (p) return {
                name: p.name,
                initials: getInitialsFromName(p.name),
                color: getAvatarColor(p.id, avatarPalette),
                subtitle: undefined,
            };
        }
        return null;
    }, [assignedUserId, assignedGroupId, assignedServiceProviderId, tuners, groupsData, serviceProvidersData, avatarPalette]);

    const isAssigned = assignmentStatus === "assigned" && assignedEntity;

    if (isMobile) {
        const moreItems: MenuProps["items"] = [
            {
                key: "settings",
                icon: <SettingOutlined />,
                label: _t("orders.settings.title"),
                onClick: onOpenSettingsModal,
            },
            ...(canDownload ? [
                {
                    key: "download-ini",
                    icon: <DownloadOutlined />,
                    label: _t("orders.titles.downloadIni"),
                    onClick: onDownloadIni,
                },
                {
                    key: "download-ini-files",
                    icon: <DownloadOutlined />,
                    label: _t("orders.titles.downloadIniAndCustomerFiles"),
                    onClick: onDownloadIniAndClientFiles,
                },
            ] : []),
            ...(isAdmin && orderStatus === "completed" ? [
                {
                    key: "open-sav",
                    icon: <MedicineBoxOutlined />,
                    label: _t("orders.actions.ticketSav"),
                    onClick: onOpenSAV,
                },
            ] : []),
            ...(isAdmin && orderStatus === "sav" ? [
                {
                    key: "close-sav",
                    icon: <CloseCircleOutlined />,
                    label: _t("orders.actions.closeTicket"),
                    onClick: onCloseSAV,
                },
            ] : []),
        ];

        const showRow2 = canShowAssignToMe || hasAvailableAutomation || isAdmin;

        return (
            <Flex vertical gap={6} className="mb-2">
                {/* Row 1: Back + Title + Status + More */}
                <Flex align="center" gap={8}>
                    <BackButton />
                    <Typography.Text
                        strong
                        ellipsis
                        style={{ flex: 1, fontSize: 16, color: token.colorPrimaryText }}
                    >
                        {_t("orders.fields.request")} #{record?.id}
                    </Typography.Text>
                    <StatusLabels value={orderStatus} />
                    <Dropdown menu={{ items: moreItems }} trigger={["click"]} placement="bottomRight">
                        <Button icon={<EllipsisOutlined />} size="small" shape="circle" />
                    </Dropdown>
                </Flex>

                {/* Row 2: Primary actions + assignment inline */}
                {showRow2 && (
                    <Flex align="center" gap={8} className="pl-8">
                        {canShowAssignToMe && (
                            <Button
                                type="primary"
                                icon={<UserOutlined />}
                                onClick={onAssignToMe}
                                loading={isAssigningToMe}
                                size="small"
                            >
                                {_t("orders.actions.assignToMe")}
                            </Button>
                        )}
                        {hasAvailableAutomation && (
                            <Button
                                icon={<ThunderboltOutlined />}
                                onClick={onOpenAutomationModal}
                                size="small"
                            >
                                {_t("orders.automation.availableBadge")}
                            </Button>
                        )}
                        {/* Assignment inline chip */}
                        {isAdmin && isAssigned && (
                            <Flex
                                align="center"
                                gap={6}
                                style={{
                                    flex: 1,
                                    background: token.colorFillAlter,
                                    borderRadius: 6,
                                    padding: "3px 8px 3px 4px",
                                    minWidth: 0,
                                }}
                            >
                                <Avatar
                                    size={20}
                                    shape="square"
                                    style={{
                                        backgroundColor: assignedEntity.color,
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {assignedEntity.initials}
                                </Avatar>
                                <Typography.Text
                                    ellipsis
                                    style={{ fontSize: 12, flex: 1, minWidth: 0 }}
                                >
                                    {assignedEntity.name}
                                </Typography.Text>
                                <Button
                                    icon={<EditOutlined style={{ fontSize: 11 }} />}
                                    size="small"
                                    type="text"
                                    style={{ padding: 0, height: "auto", minWidth: 0 }}
                                    onClick={onOpenAssignmentModal}
                                />
                            </Flex>
                        )}
                        {isAdmin && !isAssigned && (
                            <Button size="small" onClick={onOpenAssignmentModal}>
                                {_t("orders.titles.assign")}
                            </Button>
                        )}
                    </Flex>
                )}
            </Flex>
        );
    }

    return (
        <Flex vertical className="mb-4">
            <Flex justify="space-between">
                <Flex align="center">
                    <BackButton />
                    <Flex vertical className="ml-2">
                        <Typography.Title
                            level={3}
                            style={{ color: token.colorPrimaryText }}
                            className="m-0 mt-2"
                        >
                            {_t("orders.fields.request")} #{record?.id}
                        </Typography.Title>
                    </Flex>
                    <div className="ml-4">
                        <StatusLabels value={orderStatus} size="text-base" />
                    </div>
                </Flex>
                <Flex gap="small" align="center" justify="flex-end">
                    <Space.Compact>
                        {canShowAssignToMe && (
                            <Button
                                type="primary"
                                icon={<UserOutlined />}
                                onClick={onAssignToMe}
                                loading={isAssigningToMe}
                                size="middle"
                                className="shadow-none"
                            >
                                {_t("orders.actions.assignToMe")}
                            </Button>
                        )}
                        {(isAdmin || isTuner) && (
                            <Tooltip title={_t("orders.settings.title")}>
                                <Button
                                    type="primary"
                                    icon={<SettingOutlined />}
                                    onClick={onOpenSettingsModal}
                                    size="middle"
                                    className="shadow-none"
                                />
                            </Tooltip>
                        )}
                    </Space.Compact>
                    {canDownload && (
                        <Space.Compact>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={onDownloadIni}
                                size="middle"
                                type="primary"
                                className="shadow-none"
                            >
                                {_t("orders.titles.downloadIni")}
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={onDownloadIniAndClientFiles}
                                size="middle"
                                type="primary"
                                variant="text"
                                className="shadow-none"
                            >
                                {_t("orders.titles.downloadIniAndCustomerFiles")}
                            </Button>
                        </Space.Compact>
                    )}
                    {isAdmin && orderStatus === "completed" && (
                        <Button
                            type="primary"
                            icon={<MedicineBoxOutlined />}
                            onClick={onOpenSAV}
                            className="shadow-none"
                        >
                            {_t("orders.actions.ticketSav")}
                        </Button>
                    )}
                    {isAdmin && orderStatus === "sav" && (
                        <Button type="primary" icon={<CloseCircleOutlined />} onClick={onCloseSAV}>
                            {_t("orders.actions.closeTicket")}
                        </Button>
                    )}
                </Flex>
            </Flex>
            <Flex className="ml-16">
                {hasAvailableAutomation && (
                    <Button
                        icon={<ThunderboltOutlined />}
                        onClick={onOpenAutomationModal}
                        className="px-2 h-auto text-sm"
                    >
                        {_t("orders.automation.availableBadge")}
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};
